import { getSession, handleUnauthorized, refreshSession } from '../auth/session';

const BASE_URL = '/api/v1';

/** Fallback de desenvolvimento (DevAuthAdapter) — nunca usado em build de produção. */
const DEV_USER_ID =
  (import.meta.env.VITE_DEV_AUTH_USER_ID as string | undefined) ?? 'dirigente-demo';
const DEV_HOUSE_ID =
  (import.meta.env.VITE_DEV_AUTH_HOUSE_ID as string | undefined) ??
  (import.meta.env.VITE_HOUSE_ID as string | undefined) ??
  '';

function authHeaders(): Record<string, string> {
  const session = getSession();
  if (session) {
    const headers: Record<string, string> = {
      Authorization: `Bearer ${session.accessToken}`,
    };
    if (session.houseId) {
      headers['x-tenant-id'] = session.houseId;
    }
    return headers;
  }
  if (import.meta.env.DEV) {
    const headers: Record<string, string> = {
      Authorization: `Bearer ${DEV_USER_ID}`,
    };
    if (DEV_HOUSE_ID) {
      headers['x-tenant-id'] = DEV_HOUSE_ID;
    }
    return headers;
  }
  return {};
}

/**
 * 401 com sessão ativa: tenta renovar o access token uma vez e sinaliza
 * retry; se a renovação falhar, a sessão realmente caiu — volta ao login.
 */
async function recoverFrom401(): Promise<boolean> {
  if (!getSession()) return false;
  if (await refreshSession()) return true;
  handleUnauthorized();
  return false;
}

async function doFetch(path: string, options?: RequestInit): Promise<Response> {
  const headers: Record<string, string> = { ...authHeaders() };
  const isFormData = typeof FormData !== 'undefined' && options?.body instanceof FormData;
  // Sem corpo não há Content-Type: o Fastify recusa JSON vazio (FST_ERR_CTP_EMPTY_JSON_BODY).
  if (!isFormData && options?.body != null) {
    headers['Content-Type'] = 'application/json';
  }
  if (options?.headers) {
    const incoming = options.headers as Record<string, string>;
    for (const key of Object.keys(incoming)) {
      const val = incoming[key];
      if (val !== undefined) headers[key] = val;
    }
  }
  return fetch(`${BASE_URL}${path}`, { ...options, headers });
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  let res = await doFetch(path, options);
  if (res.status === 401 && (await recoverFrom401())) {
    res = await doFetch(path, options);
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      errors?: { message?: string; code?: string }[];
    };
    const msg = body.errors?.[0]?.message ?? 'Erro inesperado';
    throw new ApiError(msg, res.status, body.errors?.[0]?.code);
  }
  if (res.status === 204) return undefined as T;
  const contentType = res.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    return res.json() as Promise<T>;
  }
  return res as unknown as T;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
  ) {
    super(message);
  }
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown, headers?: Record<string, string>) =>
    request<T>(path, {
      method: 'POST',
      body: body instanceof FormData ? body : body != null ? JSON.stringify(body) : undefined,
      headers,
    }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body != null ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  getBlob: async (path: string): Promise<Blob> => {
    let res = await fetch(`${BASE_URL}${path}`, { headers: authHeaders() });
    if (res.status === 401 && (await recoverFrom401())) {
      res = await fetch(`${BASE_URL}${path}`, { headers: authHeaders() });
    }
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as {
        errors?: { message?: string; code?: string }[];
      };
      const msg = body.errors?.[0]?.message ?? 'Erro inesperado';
      throw new ApiError(msg, res.status, body.errors?.[0]?.code);
    }
    return res.blob();
  },
};
